import React, { useEffect, useMemo } from 'react';

import { Button, MenuItem, TextField } from '@material-ui/core';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import * as yup from 'yup';

import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import ColoredButton from 'components/ColoredButton';
import FormAutocomplete from 'components/FormAutocomplete/FormAutocomplete';
import FormTextInput from 'components/FormTextInput';
import assetRelationsApi from 'config/api/assetRelations/assetRelations';
import {
  AssetRelation,
  AssetRelationCategory,
  AssetRelationInput,
  AssetRelationType,
} from 'config/api/assetRelations/assetRelations.types';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import getShortLanguage from 'helpers/getShortLanguage';
import assets_messages from 'messages/assets_messages';
import general_messages from 'messages/general_messages';
import validation_messages from 'messages/validation_messages';
import assetOptionsParser from 'services/autocompleteServices/assetOptionsParser/assetOptionsParser';
import assetParamsGetter from 'services/autocompleteServices/assetParamsGetter/assetParamsGetter';
import assetResultComponent from 'services/autocompleteServices/assetResultComponent/assetResultComponent';
import { Id } from 'types/Id';

import DialogGrid from '../_components/DialogGrid';

const FORM_ID = 'asset-relation-form';

type Props = {
  open: boolean;
  onClose: () => void;
  relation?: AssetRelation | null;
  fixedAssetId?: Id;
  fixedAssetName?: string;
};

type FormData = {
  assetA: any[];
  assetB: any[];
  category: string;
  systemRelationTypeKey: string;
  orientation: 'direction_1' | 'direction_2';
  description: string;
};

type MutationInput = AssetRelationInput | { assetId: Id; input: AssetRelationInput } | { id: Id; input: AssetRelationInput };

const toOption = (asset?: any) =>
  asset
    ? {
        key: asset.id,
        id: asset.id,
        name: asset.name,
        category: [null, asset.category?.name],
      }
    : null;

const getInitialAssetA = (relation?: AssetRelation | null, fixedAssetId?: Id, fixedAssetName?: string) => {
  if (relation?.asset1) return [toOption(relation.asset1)];
  if (fixedAssetId) return [{ key: fixedAssetId, id: fixedAssetId, name: fixedAssetName || '', category: [null, ''] }];
  return [];
};

const AssetRelationDialog: React.FC<Props> = ({ open, onClose, relation, fixedAssetId, fixedAssetName }) => {
  const { i18n, t } = useTranslation();
  const language = getShortLanguage(i18n.resolvedLanguage || i18n.language);
  const queryClient = useQueryClient();
  const isEdit = !!relation;

  const { data: categories = [] } = useQuery<AssetRelationCategory[]>([QUERY_KEYS.ASSET_RELATIONS_GET_CATEGORIES, language], () =>
    assetRelationsApi.getCategories(language),
  );
  const { data: relationTypes = [] } = useQuery<AssetRelationType[]>([QUERY_KEYS.ASSET_RELATIONS_GET_TYPES, 'all', language], () =>
    assetRelationsApi.getTypes(undefined, language),
  );

  const mutation = useMutation<AssetRelation, unknown, MutationInput>(input => {
    if ('id' in input) return assetRelationsApi.update(input);
    if ('assetId' in input) return assetRelationsApi.createForAsset(input);
    return assetRelationsApi.create(input);
  });

  const formik = useFormik<FormData>({
    initialValues: {
      assetA: getInitialAssetA(relation, fixedAssetId, fixedAssetName),
      assetB: relation?.asset2 ? [toOption(relation.asset2)] : [],
      category: relation?.relationType?.categoryKey || '',
      systemRelationTypeKey: relation?.systemRelationTypeKey || '',
      orientation: 'direction_1',
      description: relation?.description || '',
    },
    validationSchema: yup.object({
      assetA: yup.array().min(1, t(validation_messages.required)),
      assetB: yup.array().min(1, t(validation_messages.required)),
      category: yup.string().required(t(validation_messages.required)),
      systemRelationTypeKey: yup.string().required(t(validation_messages.required)),
      description: yup.string().max(1000),
    }),
    onSubmit: async values => {
      const input: AssetRelationInput = {
        assetA: values.assetA[0].key,
        assetB: values.assetB[0].key,
        systemRelationTypeKey: values.systemRelationTypeKey,
        orientation: relationTypes.find((type: AssetRelationType) => type.key === values.systemRelationTypeKey)?.isSymmetric
          ? 'direction_1'
          : values.orientation,
        description: values.description,
      };
      if (isEdit && relation) {
        await mutation.mutateAsync({ id: relation.id, input });
      } else if (fixedAssetId) {
        await mutation.mutateAsync({ assetId: fixedAssetId, input });
      } else {
        await mutation.mutateAsync(input);
      }
      await queryClient.invalidateQueries(QUERY_KEYS.ASSET_RELATIONS_GET_LIST);
      await queryClient.invalidateQueries(QUERY_KEYS.ASSETS_GET_SINGLE);
      onClose();
    },
  });

  const filteredTypes = useMemo(
    () => relationTypes.filter((type: AssetRelationType) => !formik.values.category || type.categoryKey === formik.values.category),
    [relationTypes, formik.values.category],
  );
  const selectedType = relationTypes.find((type: AssetRelationType) => type.key === formik.values.systemRelationTypeKey);

  useEffect(() => {
    if (
      formik.values.systemRelationTypeKey &&
      !filteredTypes.some((type: AssetRelationType) => type.key === formik.values.systemRelationTypeKey)
    ) {
      formik.setFieldValue('systemRelationTypeKey', '');
    }
  }, [formik.values.category]);

  const sameAssetSelected = formik.values.assetA[0]?.key && formik.values.assetA[0]?.key === formik.values.assetB[0]?.key;
  const directionOptions = selectedType
    ? [
        {
          value: 'direction_1',
          label: `${formik.values.assetA[0]?.name || t(assets_messages.relations.asset_1)} ${selectedType.direction1} ${
            formik.values.assetB[0]?.name || t(assets_messages.relations.asset_2)
          }`,
        },
        {
          value: 'direction_2',
          label: `${formik.values.assetA[0]?.name || t(assets_messages.relations.asset_1)} ${selectedType.direction2} ${
            formik.values.assetB[0]?.name || t(assets_messages.relations.asset_2)
          }`,
        },
      ]
    : [];

  const disabled = mutation.isLoading;

  return (
    // @ts-ignore
    <DialogGrid
      dialogActions={
        <CenteredGrid gridGap={2} withoutPadding>
          {/* @ts-ignore */}
          <ColoredButton
            customColor='secondary'
            disabled={!formik.isValid || sameAssetSelected || disabled}
            form={FORM_ID}
            type='submit'
            variant='outlined'
          >
            {t(isEdit ? general_messages.save : general_messages.add)}
          </ColoredButton>
          <Button onClick={onClose} size='small'>
            {t(general_messages.cancel)}
          </Button>
        </CenteredGrid>
      }
      onClose={onClose}
      open={open}
      title={t(isEdit ? assets_messages.relations.edit_relation : assets_messages.relations.new_relation)}
    >
      <form id={FORM_ID} onSubmit={formik.handleSubmit}>
        <CenteredGrid gridGap={2} withoutPadding>
          <FormAutocomplete
            apiCallParamsGetter={assetParamsGetter}
            customizeLabel={assetResultComponent}
            disabled={disabled || (!!fixedAssetId && !isEdit)}
            formik={formik}
            id='assetA'
            initialOptions={formik.values.assetA}
            label={t(assets_messages.relations.asset_1)}
            minSearchLength={2}
            multiple={false}
            optionsParser={assetOptionsParser}
          />
          <FormAutocomplete
            apiCallParamsGetter={assetParamsGetter}
            customizeLabel={assetResultComponent}
            disabled={disabled}
            formik={formik}
            helperText={sameAssetSelected ? t(assets_messages.relations.same_asset_error) : undefined}
            id='assetB'
            initialOptions={formik.values.assetB}
            label={t(assets_messages.relations.asset_2)}
            minSearchLength={2}
            multiple={false}
            optionsParser={assetOptionsParser}
          />
          <TextField
            disabled={disabled}
            fullWidth
            label={t(assets_messages.relations.category)}
            name='category'
            onChange={formik.handleChange}
            select
            value={formik.values.category}
            variant='outlined'
          >
            {categories.map((category: AssetRelationCategory) => (
              <MenuItem key={category.key} value={category.key}>
                {category.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            disabled={disabled || !formik.values.category}
            fullWidth
            label={t(assets_messages.relations.relation_type)}
            name='systemRelationTypeKey'
            onChange={formik.handleChange}
            select
            value={formik.values.systemRelationTypeKey}
            variant='outlined'
          >
            {filteredTypes.map((type: AssetRelationType) => (
              <MenuItem key={type.key} value={type.key}>
                {type.name}
              </MenuItem>
            ))}
          </TextField>
          {selectedType && !selectedType.isSymmetric && (
            <TextField
              disabled={disabled}
              fullWidth
              label={t(assets_messages.relations.direction)}
              name='orientation'
              onChange={formik.handleChange}
              select
              value={formik.values.orientation}
              variant='outlined'
            >
              {directionOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          <FormTextInput
            disabled={disabled}
            formik={formik}
            fullWidth
            id='description'
            inputProps={{ maxLength: 1000 }}
            label={t(assets_messages.relations.description)}
            multiline
            rows={4}
          />
        </CenteredGrid>
      </form>
    </DialogGrid>
  );
};

export default AssetRelationDialog;
